import styled from "styled-components";
import Skeleton from "@mui/material/Skeleton";
import theme from "../../styles/theme";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";

const BlockTitle = styled.div`
  font-size: 32px;
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
  margin-bottom: 20px;
  padding-bottom: 0px;
  width: 100%;
  display: flex;
  justify-content: row;
  align-items: end;
`;

const ImageContainer = styled.div`
  max-width: 320px;
  width: 100%;
  text-align: center;
  height: 150px;
  padding-left: 96px;

  @media (max-width: ${theme.breakpointsCustom.lg}px) {
    padding-left: 0px;
    max-width: none;
    width: fit-content;
    height: 100%;
    display: flex;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-left: 16px;
`;

const MainContainer = styled.div`
  display: flex;

  @media (max-width: ${theme.breakpointsCustom.lg}px) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 24px;
  }
`;

const LeftSideContainer = styled.div`
  background: #2a2a2ac4;
  max-width: 320px;
  height: fit-content;
  width: 100%;
  border: 2px solid ${theme.colors.secondaryDark};
  padding: 16px 16px 16px 0px;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  gap: 15px;

  @media (max-width: ${theme.breakpointsCustom.lg}px) {
    width: 100%;
    max-width: none;
  }
`;

const RowContainer = styled.div`
  border-bottom: 2px solid ${theme.colors.secondaryDark};
  padding-bottom: 8px;
`;

const RightSideContainer = styled.div`
  margin-left: 16px;
  width: 100%;
  position: relative;

  @media (max-width: ${theme.breakpointsCustom.lg}px) {
    margin-left: 0px;
  }
`;

const TableTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  padding: 16px;
  color: ${theme.colors.white};
`;

const SkeletonTableCell = styled(Skeleton)`
  background-color: rgba(255, 255, 255, 0.11);
`;

const tableContainerStyles = {
  background: "#2a2a2ac4",
  border: `2px solid ${theme.colors.secondaryDark}`,
  borderRadius: "4px",
  marginBottom: "24px",
};

const DriverProfileSkeleton = () => {
  return (
    <div style={{ marginTop: 24 }}>
      {/* Breadcrumbs skeleton */}
      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <Skeleton variant="text" width={80} height={16} />
        <Skeleton variant="text" width={10} height={16} />
        <Skeleton variant="text" width={80} height={16} />
        <Skeleton variant="text" width={10} height={16} />
        <Skeleton variant="text" width={120} height={16} />
      </div>

      <div style={{ marginTop: 24 }}>
        <BlockTitle>
          <ImageContainer>
            <Skeleton variant="circular" width={150} height={150} />
          </ImageContainer>
          <TitleContainer>
            <Skeleton variant="text" width={200} height={60} />
          </TitleContainer>
        </BlockTitle>

        <MainContainer style={{ marginTop: 36 }}>
          <LeftSideContainer>
            {/* Team skeleton */}
            <RowContainer>
              <div
                style={{ display: "flex", justifyContent: "space-between", paddingLeft: "24px" }}
              >
                <Skeleton variant="text" width={80} height={16} />
                <div style={{ display: "flex", gap: "8px" }}>
                  <Skeleton variant="text" width={64} height={16} />
                  <Skeleton variant="circular" width={16} height={16} />
                </div>
              </div>
            </RowContainer>

            {/* Social media skeleton */}
            <RowContainer>
              <div
                style={{ display: "flex", justifyContent: "left", gap: "16px", padding: "0 24px" }}
              >
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton variant="circular" width={24} height={24} />
              </div>
            </RowContainer>

            {/* History skeleton */}
            <RowContainer>
              <div style={{ padding: "0 24px" }}>
                <Skeleton variant="text" width={80} height={16} />
                <div style={{ paddingLeft: "32px" }}>
                  <div style={{ display: "flex", justifyContent: "end" }}>
                    <Skeleton variant="text" width={80} height={20} />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <Skeleton variant="text" width={60} height={16} />
                    <Skeleton variant="text" width={40} height={16} />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <Skeleton variant="text" width={60} height={16} />
                    <Skeleton variant="text" width={40} height={16} />
                  </div>
                </div>
              </div>
            </RowContainer>

            {/* Achievements skeleton */}
            <RowContainer style={{ borderBottom: "none" }}>
              <div style={{ padding: "0 24px" }}>
                <Skeleton variant="text" width={120} height={16} />
                <Skeleton
                  variant="text"
                  width="100%"
                  height={24}
                  style={{ marginTop: "8px", borderRadius: "4px" }}
                />
                <Skeleton
                  variant="text"
                  width="100%"
                  height={24}
                  style={{ marginTop: "4px", borderRadius: "4px" }}
                />
              </div>
            </RowContainer>
          </LeftSideContainer>

          <RightSideContainer>
            {/* Recent Events Table Skeleton */}
            <TableContainer
              component={Paper}
              sx={{
                ...tableContainerStyles,
                marginBottom: "24px",
              }}
            >
              <TableTitle>
                <Skeleton variant="text" width={150} height={28} />
              </TableTitle>
              <Table sx={{ minWidth: 650 }} aria-label="recent events table skeleton">
                <TableHead>
                  <TableRow>
                    <th style={{ padding: "12px 16px" }}>
                      <SkeletonTableCell variant="text" width={40} height={16} />
                    </th>
                    <th style={{ padding: "12px 8px" }}>
                      <SkeletonTableCell variant="text" width={40} height={16} />
                    </th>
                    <th style={{ padding: "12px 8px", textAlign: "center" }}>
                      <SkeletonTableCell variant="text" width={80} height={16} />
                    </th>
                    <th style={{ padding: "12px", textAlign: "center" }}>
                      <SkeletonTableCell variant="text" width={40} height={16} />
                    </th>
                    <th style={{ padding: "12px", textAlign: "center" }}>
                      <SkeletonTableCell variant="text" width={40} height={16} />
                    </th>
                    <th style={{ padding: "12px", textAlign: "center" }}>
                      <SkeletonTableCell variant="text" width={40} height={16} />
                    </th>
                    <th style={{ padding: "12px", textAlign: "center" }}>
                      <SkeletonTableCell variant="text" width={75} height={16} />
                    </th>
                    <th style={{ padding: "12px 16px", textAlign: "center" }}>
                      <SkeletonTableCell variant="text" width={75} height={16} />
                    </th>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...Array(5)].map((_, index) => (
                    <TableRow key={index}>
                      <td style={{ padding: "12px 16px" }}>
                        <SkeletonTableCell variant="text" width={70} height={16} />
                      </td>
                      <td style={{ padding: "12px 8px" }}>
                        <SkeletonTableCell variant="text" width={300} height={16} />
                      </td>
                      <td style={{ padding: "12px 8px" }}>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <SkeletonTableCell
                              variant="circular"
                              width={16}
                              height={16}
                              style={{ marginRight: "8px" }}
                            />
                            <SkeletonTableCell variant="text" width={84} height={16} />
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: "12px", textAlign: "center" }}>
                        <SkeletonTableCell variant="text" width={40} height={16} />
                      </td>
                      <td style={{ padding: "12px", textAlign: "center" }}>
                        <SkeletonTableCell variant="text" width={40} height={16} />
                      </td>
                      <td style={{ padding: "12px", textAlign: "center" }}>
                        <SkeletonTableCell variant="text" width={40} height={16} />
                      </td>
                      <td style={{ padding: "12px", textAlign: "center" }}>
                        <SkeletonTableCell variant="text" width={75} height={16} />
                      </td>
                      <td style={{ padding: "12px 16px", textAlign: "center" }}>
                        <SkeletonTableCell variant="text" width={75} height={16} />
                      </td>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Career Table Skeleton */}
            <TableContainer
              component={Paper}
              sx={{
                ...tableContainerStyles,
                marginBottom: "24px",
              }}
            >
              <TableTitle>
                <Skeleton variant="text" width={100} height={28} />
              </TableTitle>
              <Table sx={{ minWidth: 650 }} aria-label="career table skeleton">
                <TableHead>
                  <TableRow>
                    <th style={{ padding: "12px 16px" }}>
                      <SkeletonTableCell variant="text" width={36} height={16} />
                    </th>
                    <th style={{ padding: "12px 0px" }}>
                      <SkeletonTableCell variant="text" width={48} height={16} />
                    </th>
                    <th style={{ padding: "12px 0px" }}>
                      <SkeletonTableCell variant="text" width={120} height={16} />
                    </th>
                    <th style={{ padding: "12px 0px", textAlign: "center" }}>
                      <SkeletonTableCell variant="text" width={50} height={16} />
                    </th>
                    <th style={{ padding: "12px 0px", textAlign: "center" }}>
                      <SkeletonTableCell variant="text" width={50} height={16} />
                    </th>
                    <th style={{ padding: "12px 0px", textAlign: "center" }}>
                      <SkeletonTableCell variant="text" width={120} height={16} />
                    </th>
                    <th style={{ padding: "12px 16px", textAlign: "center" }}>
                      <SkeletonTableCell variant="text" width={120} height={16} />
                    </th>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...Array(3)].map((_, index) => (
                    <TableRow key={index}>
                      <td style={{ padding: "12px 16px" }}>
                        <SkeletonTableCell variant="text" width={150} height={16} />
                      </td>
                      <td style={{ padding: "12px 0px" }}>
                        <SkeletonTableCell variant="text" width={100} height={16} />
                      </td>
                      <td style={{ padding: "12px 0px" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <SkeletonTableCell
                              variant="circular"
                              width={16}
                              height={16}
                              style={{ marginRight: "8px" }}
                            />
                            <SkeletonTableCell variant="text" width={96} height={16} />
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: "12px 0px", textAlign: "center" }}>
                        <SkeletonTableCell variant="text" width={50} height={16} />
                      </td>
                      <td style={{ padding: "12px 0px", textAlign: "center" }}>
                        <SkeletonTableCell variant="text" width={50} height={16} />
                      </td>
                      <td style={{ padding: "12px 0px", textAlign: "center" }}>
                        <SkeletonTableCell variant="text" width={120} height={16} />
                      </td>
                      <td style={{ padding: "12px 16px", textAlign: "center" }}>
                        <SkeletonTableCell variant="text" width={120} height={16} />
                      </td>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </RightSideContainer>
        </MainContainer>
      </div>
    </div>
  );
};

export default DriverProfileSkeleton;
